// =============================================================================
// Optim TURPE V2 — le vocabulaire montré au client (DEV-1913).
//
// Tous les libellés de ce fichier sont des **PROPOSITIONS** reprises du contrat
// cible (§8.5 et §9) et restent à valider par Mathieu (texte-ok) avant la
// release. Ils vivent ici, en un seul endroit, pour qu'une relecture les
// corrige d'un seul geste.
//
// Deux interdits tenus par ce fichier :
// - le mot « fiabilité faible » n'existe nulle part (DEV-848) : on décrit la
//   DONNÉE et ce qu'on en fait, jamais la valeur de l'étude ;
// - une cause inconnue n'est pas habillée d'une phrase rassurante : on rend
//   `null` et l'appelant affiche le code servi, ce qui est encore une
//   information vraie.
// =============================================================================

import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import type {
    OptimTurpeV2EtatServi,
    OptimTurpeV2NiveauDonnees,
    OptimTurpeV2Provenance,
    OptimTurpeV2VerdictDenivele,
} from "@modules/courtage/types/optim-turpe-v2"

/**
 * Libellés du niveau de qualité (§8.5). On décrit la courbe de charge et ce que
 * l'étude en fait. Le libellé du niveau le plus bas dit « montant non servi », il
 * ne juge pas la fiabilité de quoi que ce soit.
 */
export const LIBELLE_NIVEAU_DONNEES: Record<OptimTurpeV2NiveauDonnees, string> =
    {
        fiable: "Courbe de charge complète",
        reserve: "Courbe de charge quasi complète",
        indicatif:
            "Courbe de charge partielle : recommandation donnée sous réserve",
        // Vague wording du 14/10/2026 (Q13, Q21) : sous une économie non
        // chiffrée, aucune puissance n'est dite « recommandée ».
        faible: "Courbe de charge très incomplète : économie non chiffrée",
    }

/**
 * Libellés courts des états servis (§2.3), lexique de la vague wording du
 * 14/10/2026 (Q12, Q13, Q16, partie 4 du dossier des arbitrages) : un seul nom
 * du montant, « Économie estimée » ; sans gain, « Rien à changer sur
 * l'acheminement » (déjà optimale comme surcoût, tableau des titres par état) ;
 * sans montant, « Économie non chiffrée », suivi de sa cause là où elle se dit.
 */
export const LIBELLE_ETAT: Record<OptimTurpeV2EtatServi, string> = {
    economie: "Économie estimée",
    gain_par_depassements_evites:
        "Économie estimée, dépassements (CMDPS) évités compris",
    surcout: "Rien à changer sur l'acheminement",
    deja_optimale: "Rien à changer sur l'acheminement",
    montant_non_servi: "Économie non chiffrée",
}

/**
 * La ligne de périmètre sous chaque titre du résultat (Q17, règle 3 de
 * Mélodie) : l'étude porte sur l'acheminement seul.
 */
export const LIGNE_DE_PERIMETRE =
    "Acheminement (TURPE) seul, hors TVA, CTA comprise. La fourniture n'est pas étudiée."

/** Ce que chaque motif de surcoût explique au client (§2.3, A5). */
export const LIBELLE_ETAT_MOTIF: Record<string, string> = {
    scenario_libre:
        "Ce montant est celui de vos puissances, chiffrées telles quelles.",
    couverture_du_pic:
        "Couvrir les dépassements coûte plus cher que de les payer comme aujourd'hui.",
    // Finitions du 07/10/2026 : la marge de sécurité est de 5 % par défaut
    // (courtage#289), elle n'est donc plus « demandée » ; le moteur nomme ce
    // motif dès qu'une marge est posée (`restitution._motif_surcout`), sans
    // prouver qu'elle seule fait le surcoût : « lié à », pas « vient de ». La
    // marge relève la pointe mesurée (maquette de l'étude allégée) ; elle se
    // règle par « Prudence », dans « Modifier les paramètres ».
    marge_de_securite:
        "Surcoût lié à la marge de sécurité : les puissances chiffrées couvrent la puissance maximale atteinte, relevée de cette marge, réglable dans « Modifier les hypothèses ».",
    borne_levee:
        "Ce surcoût vient d'une puissance autorisée au-dessus du raccordement.",
    arbitrage_des_depassements:
        "Ce surcoût vient du scénario retenu pour les dépassements.",
    // courtage#254 (DEV-2183) : sous « déjà optimale », le motif est servi.
    // A7 (14/10/2026) : il ne s'affiche plus sous le titre, que l'option
    // chiffrée sous le seuil dit déjà ; le texte reste pour le rapport d'équipe.
    ecart_dans_la_bande_du_seuil:
        "L'écart avec le contrat actuel reste sous le seuil : aucune démarche.",
}

/** Lecture du dénivelé PROPOSÉ devant les appels mesurés (courtage#254, M7). */
export const LIBELLE_VERDICT_DENIVELE_RECOMMANDE: Record<string, string> = {
    couvre_la_pointe: "Couvre la puissance maximale atteinte",
    sous_la_pointe:
        "Sous la puissance maximale atteinte : des dépassements sont attendus sur ce poste",
}

/** Cause d'un état absent, servie par le moteur (`restitution.etat_cause`). */
export const LIBELLE_ETAT_CAUSE: Record<string, string> = {
    "economie-non-calculable":
        "Le montant n'a pas pu être calculé sur cette étude.",
    "etat-incoherent-avec-le-montant":
        "Le résultat de cette étude contredit son montant : aucun chiffre n'est affiché plutôt qu'un chiffre douteux.",
}

/**
 * Causes de « montant non servi » (§9.2, A9), écrites en titre « Économie non
 * chiffrée : cause » (vague wording du 14/10/2026, Q13). Q21 : sans montant,
 * aucune puissance n'est dite recommandée ni « valable ».
 */
export const LIBELLE_MONTANT_NON_SERVI: Record<string, string> = {
    "montant-non-servi-couverture":
        "Économie non chiffrée : la part mesurée de la courbe de charge est insuffisante sur la période.",
    "montant-non-servi-mois-absents":
        "Économie non chiffrée : un ou plusieurs mois de la période n'ont aucune mesure.",
    "montant-non-servi-saison-haute-non-mesuree":
        "Économie non chiffrée : la saison haute de la période (novembre à mars) n'est pas suffisamment mesurée.",
    "montant-non-servi-bloc-long":
        "Économie non chiffrée : la période porte un bloc de jours entiers sans aucune mesure, trop long pour établir un montant.",
    // Cause servie depuis la reprise par classe temporelle (L3/L31) : sans
    // libellé ici, le client lirait le code brut.
    "montant-non-servi-classe-sous-observee":
        "Économie non chiffrée : un ou plusieurs postes ne sont pas assez mesurés sur la période.",
    // Revue d'ouverture du 12/10/2026 (Q8) : la puissance souscrite actuelle
    // dépasse le raccordement ou le plafond du segment ; elle ne peut pas
    // servir de base au montant. Sans lien avec la mesure de la courbe : la
    // phrase ne la cite pas (absente de la table ci-dessous).
    "montant-non-servi-base-de-comparaison-impossible":
        "Économie non chiffrée : la puissance souscrite actuelle dépasse ce que le compteur peut porter, elle ne peut donc pas servir de base de comparaison.",
}

/**
 * Revue 2 en production du 15/10/2026 (I41, I85, I91, lot A6) : le verdict de
 * la carte tient sur UNE ligne à 1440 px (45 caractères au plus) et sur deux
 * lignes à 560 px. « Économie non chiffrée : cause » (lexique) garde sa forme,
 * la cause en quelques mots ; la phrase complète de la cause passe dans la
 * raison, sous le verdict, quand la mesure ne la dit pas déjà.
 */
export const VERDICT_NON_CHIFFRE_COURT: Record<string, string> = {
    "montant-non-servi-couverture":
        "Économie non chiffrée : mesures insuffisantes",
    "montant-non-servi-mois-absents":
        "Économie non chiffrée : des mois sans mesure",
    "montant-non-servi-saison-haute-non-mesuree":
        "Économie non chiffrée : trou en saison haute",
    "montant-non-servi-bloc-long":
        "Économie non chiffrée : trou de mesure long",
    "montant-non-servi-classe-sous-observee":
        "Économie non chiffrée : postes peu mesurés",
    "montant-non-servi-base-de-comparaison-impossible":
        "Économie non chiffrée : contrat hors limites",
}

/**
 * Les causes de « montant non servi » qui tiennent à la mesure : la raison du
 * verdict court les dit par les jours mesurés (« 253 jours mesurés sur
 * 365… ») ; les autres causes gardent leur phrase complète en raison.
 */
export const CAUSES_NON_CHIFFRE_DE_MESURE: readonly string[] = [
    "montant-non-servi-couverture",
    "montant-non-servi-mois-absents",
    "montant-non-servi-saison-haute-non-mesuree",
    "montant-non-servi-bloc-long",
    "montant-non-servi-classe-sous-observee",
]

/**
 * Le nom court d'un scénario de la paire (charte : Couvrir / Assumer), dans
 * la raison d'une carte dont le scénario affiché n'est pas la recommandation
 * (revue 2 en production, I24, I61, lot A7).
 */
export const NOM_COURT_SCENARIO = {
    sans: "Couvrir",
    avec: "Assumer",
} as const

/** Le titre d'une économie non chiffrée dont la cause n'est pas connue de la page. */
export const ECONOMIE_NON_CHIFFREE_SANS_CAUSE =
    "Économie non chiffrée sur cette étude."

/**
 * Revue du support du 08/10 (A4) : « On dit juste que la courbe est très
 * incomplète, mais c'est juste un fait sans explication. » La phrase porte la
 * mesure quand le moteur la sert ; chaque cause s'y écrit en fin de phrase.
 */
const CAUSE_MONTANT_NON_SERVI_APRES_LA_MESURE: Record<string, string> = {
    "montant-non-servi-couverture":
        "cette part ne suffit pas à établir un montant",
    "montant-non-servi-mois-absents": "au moins un mois n'a aucune mesure",
    "montant-non-servi-saison-haute-non-mesuree":
        "la saison haute (novembre à mars) n'est pas suffisamment mesurée",
    "montant-non-servi-bloc-long":
        "la période porte un bloc de jours entiers sans aucune mesure, trop long pour établir un montant",
    "montant-non-servi-classe-sous-observee":
        "un ou plusieurs postes ne sont pas assez mesurés",
}

/**
 * Un pourcentage de mesure à une décimale, qui ne s'arrondit JAMAIS vers 100 ni
 * vers 0 (DEV-2181, M2) : 99,96 se lit « 99,9 % », jamais « 100 % », qui
 * effacerait un trou ; 0,04 se lit « 0,1 % ». `null` quand la valeur n'est pas
 * servie : une absence ne s'écrit pas.
 *
 * Une seule règle d'arrondi pour une même mesure, partout sur la page (A2 de la
 * vague wording du 14/10/2026, R10 : « 69 % » ici et « 69,2 % » là pour la même
 * couverture) : la phrase du montant non servi et le détail des mesures
 * (`optim-turpe-v2.qualite.ts`) passent tous deux par cette fonction.
 */
export function formatPctMesure(valeur: unknown): string | null {
    if (typeof valeur !== "number" || !Number.isFinite(valeur)) return null
    let v = Math.round(valeur * 10) / 10
    if (v >= 100 && valeur < 100) v = 99.9
    if (v <= 0 && valeur > 0) v = 0.1
    const texte = formateurFr({
        maximumFractionDigits: 1,
    }).format(v)
    return `${texte}\u00a0%`
}

/**
 * La phrase d'un montant non servi (A9, puis A4 de la revue du support), sous
 * le titre « Économie non chiffrée : cause » (vague wording du 14/10/2026) :
 * « La courbe de charge n'est mesurée que sur 69,2 % de la période (48 % de
 * décembre à février) et au moins un mois n'a aucune mesure. » La mesure
 * d'hiver est celle du moteur, de décembre à février (I7) ; aucune puissance
 * n'est dite « valable » (Q21) et la période proposée est un bouton.
 *
 * La mesure est la couverture MESURÉE au pas natif (`couverture_mesuree_pct`,
 * la seule opposable, DEV-1910), jamais la couverture après comblement. Sans
 * mesure servie (ou à 100 %), la phrase de la cause reste telle quelle ; une
 * cause inconnue rend `null` et l'appelant garde sa phrase.
 */
export function phraseMontantNonServi(
    cause: string,
    qualite:
        | {
              couverture_mesuree_pct?: number | null
              couverture_hiver_mesuree_pct?: number | null
              meilleure_periode?: unknown
          }
        | null
        | undefined
): string | null {
    const seule = LIBELLE_MONTANT_NON_SERVI[cause] ?? null
    const fin = CAUSE_MONTANT_NON_SERVI_APRES_LA_MESURE[cause]
    const mesure = qualite?.couverture_mesuree_pct
    const pct =
        typeof mesure === "number" && mesure < 100
            ? formatPctMesure(mesure)
            : null
    if (!fin || !pct) return seule
    const hiver = formatPctMesure(qualite?.couverture_hiver_mesuree_pct)
    return (
        `La courbe de charge n'est mesurée que sur ${pct} de la période` +
        `${hiver ? ` (${hiver} de décembre à février)` : ""} et ${fin}.`
    )
}

/** Causes d'un équivalent annuel absent (§9.2). */
export const LIBELLE_EQUIVALENT_ANNUEL_CAUSE: Record<string, string> = {
    "fenetre-trop-courte-pour-annualiser":
        "Équivalent annuel non calculé : la période analysée ne couvre pas toutes les saisons tarifaires.",
    "pas-prices-inconnus":
        "Équivalent annuel non calculé : le nombre de mesures du montant n'est pas connu.",
    "economie-non-calculable":
        "Équivalent annuel non calculé : il n'y a pas de montant à annualiser.",
}

/** Motifs d'une valeur de raccordement écartée (§4.4.2, A7). */
export const LIBELLE_RACCORDEMENT_MOTIF: Record<string, string> = {
    appels_superieurs:
        "La puissance de raccordement connue est inférieure à la puissance maximale atteinte : elle ne limite pas la recommandation.",
    unite_inconnue:
        "L'unité de la puissance de raccordement transmise n'est pas reconnue : elle n'a pas été utilisée.",
    valeur_invraisemblable:
        "La puissance de raccordement transmise n'est pas plausible pour ce compteur : elle n'a pas été utilisée.",
    unite_incompatible:
        "La puissance de raccordement est connue dans une unité qui ne peut pas limiter la recommandation sans conversion : elle n'a pas été utilisée.",
    absente:
        "La puissance de raccordement de ce compteur n'est pas connue : la recommandation n'est pas limitée par le raccordement.",
    sous_le_plancher:
        "La puissance de raccordement transmise est sous la valeur plausible la plus basse de ce segment : elle n'a pas été utilisée.",
}

/** Ce que dit chaque statut de la borne de raccordement (§4.4.2). */
export const LIBELLE_RACCORDEMENT_STATUT: Record<string, string> = {
    appliquee: "Limite appliquée à la recommandation",
    // La limite a été appliquée, mais la recommandation reste en dessous : elle
    // ne contraint rien (revue du 25/09, C4 à 108 kVA pour 95 recommandés).
    appliquee_non_atteinte:
        "Limite appliquée, non atteinte par la recommandation",
    levee: "Puissance supérieure au raccordement autorisée à votre demande",
    ecartee: "Valeur non retenue",
    absente: "Valeur inconnue",
}

/** Verdicts du contrôle de vraisemblance du dénivelé ACTUEL (§4.5). */
export const LIBELLE_VERDICT_DENIVELE: Record<
    OptimTurpeV2VerdictDenivele,
    string
> = {
    coherent: "Cohérente avec la puissance maximale atteinte",
    sous_la_pointe:
        "Sous la puissance maximale atteinte : des dépassements sont attendus avec le contrat actuel",
    tres_au_dessus_de_la_pointe:
        "Très au-dessus de la puissance maximale atteinte : vérifiez cette valeur, l'économie calculée en dépend",
    superieur_au_raccordement:
        "Au-dessus de la puissance de raccordement connue : vérifiez cette valeur",
}

/**
 * Verdict de représentation d'une classe sur la période étudiée (L3, L31, Q-A),
 * tel que `qualite_donnees.couverture_par_classe[].verdict` le sert.
 */
export const LIBELLE_VERDICT_CLASSE: Record<string, string> = {
    observee: "observée",
    "sous-observee": "partiellement observée",
    "non-observee": "non observée",
    "non-mesurable": "non mesurable",
    "hors-fenetre": "absente de la période étudiée",
}

/**
 * Le nom en clair d'une classe horaire, pour les phrases et les en-têtes que la
 * page compose (DEV-2311 : aucun code de classe seul devant le client).
 *
 * Mêmes mots que la légende déjà écrite par la page
 * (`OptimTurpeV2Demande.vue`, « PTE : heures de pointe. HPH et HCH : heures
 * pleines et heures creuses de saison haute… ») et que la table du moteur
 * (courtage `modules/mixins/data_quality.py`, `LIBELLES_DES_CLASSES`). Un code
 * absent garde son code : mieux vaut un sigle qu'un nom inventé.
 */
export const LIBELLE_CLASSE_HORAIRE: Record<string, string> = {
    PTE: "heures de pointe",
    HPH: "heures pleines de saison haute",
    HCH: "heures creuses de saison haute",
    HPB: "heures pleines de saison basse",
    HCB: "heures creuses de saison basse",
    // Les classes d'un compteur sans saisons (05/10/2026), mêmes mots que la
    // table du moteur citée ci-dessus.
    Base: "heures de base",
    HP: "heures pleines",
    HC: "heures creuses",
}

/** L'ordre des postes, de la pointe aux heures creuses de saison basse. */
const ORDRE_DES_POSTES = ["PTE", "HPH", "HCH", "HPB", "HCB"]

/**
 * Un ensemble de postes nommé en clair, codes entre parenthèses (vague
 * wording du 14/10/2026, Q6 ; schéma de Mélodie : « Postes de saison basse
 * (HPB, HCB) ») : « tous les postes » quand ils y sont tous, une saison
 * entière par son nom, sinon chaque poste en clair. `tous` : le nombre de
 * postes du compteur.
 */
export function libellePostes(codes: readonly string[], tous: number): string {
    const ordonnes = ORDRE_DES_POSTES.filter((p) => codes.includes(p))
    const inconnus = codes.filter((c) => !ORDRE_DES_POSTES.includes(c))
    const liste = [...ordonnes, ...inconnus]
    if (liste.length > 1 && liste.length === tous) return "tous les postes"
    const memes = (a: string[]) =>
        a.length === liste.length && a.every((c) => liste.includes(c))
    if (memes(["HPH", "HCH"])) return "postes de saison haute (HPH, HCH)"
    if (memes(["HPB", "HCB"])) return "postes de saison basse (HPB, HCB)"
    const noms = liste.map(libelleClasseHoraire)
    return noms.length > 1
        ? `${noms.slice(0, -1).join(", ")} et ${noms[noms.length - 1]}`
        : (noms[0] ?? "")
}

/**
 * Le POSTE en clair, toujours avec son code (vague wording du 14/10/2026, Q6) :
 * « heures creuses de saison basse (HCB) » pour `HCB` ; « heures de base »
 * pour `Base`, dont le nom dit déjà le code ; le code tel quel sinon.
 */
export function libelleClasseHoraire(classe: string): string {
    const libelle = LIBELLE_CLASSE_HORAIRE[classe]
    if (!libelle) return classe
    return classe === "Base" ? libelle : `${libelle} (${classe})`
}

/**
 * La légende d'un tableau qui écrit ses classes en code (05/10/2026) :
 * « PTE : heures de pointe. HCB : heures creuses de saison basse. », pour les
 * seules classes présentes et connues, dans l'ordre donné. Un code inconnu n'a
 * pas de ligne (il s'affiche seul dans le tableau) ; aucune classe connue, et la
 * légende est `null`.
 */
export function legendeDesClasses(classes: Iterable<string>): string | null {
    const vues = new Set<string>()
    const lignes: string[] = []
    for (const classe of classes) {
        if (vues.has(classe)) continue
        vues.add(classe)
        const libelle = LIBELLE_CLASSE_HORAIRE[classe]
        if (libelle && libelle !== classe)
            lignes.push(`${classe} : ${libelle}.`)
    }
    return lignes.length > 0 ? lignes.join(" ") : null
}

/**
 * Les libellés des formules tarifaires d'acheminement (DEV-2311 : aucun code de
 * formule seul devant le client).
 *
 * Copie de la table du moteur : courtage v183 (ee8e0009),
 * `modules/optimturpe/optimturpe_explain.py`, `LIBELLE_FORMULE_TARIFAIRE`,
 * identique mot pour mot aux onze codes ci-dessous (vérifié le 15/10/2026,
 * vague wording du 14/10/2026 : « à quatre postes » pour BTINFCU4 et
 * BTINFMU4, Q2, Q6, W62). Le moteur ne la sert pas à la page ; quand il la
 * servira, cette copie disparaîtra. Figée par
 * `optim-turpe-v2.libelles.formules.spec.ts` (les 11 codes).
 */
export const LIBELLE_FORMULE_TARIFAIRE: Readonly<Record<string, string>> = {
    BTINFCUST: "courte utilisation sans distinction d'horaire",
    // Vague wording du 14/10/2026 (Q2, Q6, W62) : la formule en clair, « à
    // quatre postes », sans code court du marché.
    BTINFCU4: "courte utilisation à quatre postes",
    BTINFMU4: "moyenne utilisation à quatre postes",
    BTINFMUDT: "moyenne utilisation en heures pleines et heures creuses",
    BTINFLU: "longue utilisation",
    BTSUPCU4: "courte utilisation",
    BTSUPLU4: "longue utilisation",
    HTACU5: "courte utilisation à pointe fixe",
    HTALU5: "longue utilisation à pointe fixe",
    HTACUPM5: "courte utilisation à pointe mobile",
    HTALUPM5: "longue utilisation à pointe mobile",
}

/**
 * « formule courte utilisation (BTSUPCU4) » : le libellé, puis le code entre
 * parenthèses (le fournisseur en a besoin). Un code inconnu s'affiche seul :
 * on n'invente pas de libellé.
 */
export function libelleFormuleTarifaire(code: string): string {
    const libelle = LIBELLE_FORMULE_TARIFAIRE[code]
    return libelle ? `formule ${libelle} (${code})` : code
}

/**
 * « formule longue utilisation à pointe fixe » : le libellé sans son code, pour
 * une ligne courte (l'en-tête « Mon contrat ») dont le corps porte le code. Un
 * code inconnu s'affiche seul.
 */
export function libelleFormuleTarifaireSansCode(code: string): string {
    const libelle = LIBELLE_FORMULE_TARIFAIRE[code]
    return libelle ? `formule ${libelle}` : code
}

/** Une provenance que la page ne sait pas nommer ne s'affiche pas en code. */
export const LIBELLE_PROVENANCE_INCONNUE = "Non précisée"

/**
 * D'où vient une entrée résolue (§3.2), en quatre valeurs (vague wording du
 * 14/10/2026, partie 4, « Source ») : Enedis, Mesure du compteur, Votre
 * saisie, Valeur par défaut. Une valeur `derive` n'a pas de source propre :
 * le moteur ne dérive que le segment, de la formule tarifaire d'acheminement
 * (courtage `registre_entrees.py`), et le registre lui donne la source de
 * cette formule (`lireRegistre`, `provenanceSource`). Lue seule, elle reste
 * « Non précisée » : rien ne prouve qu'elle vienne d'Enedis.
 */
export const LIBELLE_PROVENANCE: Record<OptimTurpeV2Provenance, string> = {
    enedis: "Enedis",
    defaut_moteur: "Valeur par défaut",
    surcharge_client: "Votre saisie",
    derive: LIBELLE_PROVENANCE_INCONNUE,
    mesure: "Mesure du compteur",
    defaut_page: "Valeur par défaut",
}

/**
 * Nom lisible d'un champ du registre. Une entrée inconnue rend son chemin tel
 * quel : un chemin technique est moins agréable qu'une phrase, mais il est vrai.
 */
export const LIBELLE_CHAMP: Record<string, string> = {
    "structure_tarifaire.formule_tarifaire_acheminement.fta":
        "Formule d'acheminement (FTA)",
    "structure_tarifaire.puissance_souscrite_soutirage.denivele":
        "Puissances souscrites par poste",
    "structure_tarifaire.puissance_souscrite_soutirage.puissance":
        "Puissance souscrite",
    "structure_tarifaire.offre_tarifaire.option_tarifaire":
        "Pointe fixe ou pointe mobile",
    "structure_tarifaire.offre_tarifaire.turpe.date":
        "Date du barème TURPE (grille tarifaire en vigueur)",
    "structure_tarifaire.offre_tarifaire.turpe.inclus_consommation":
        "TURPE inclus dans la consommation",
    "compteur.segment": "Segment tarifaire",
    "compteur.raccordement.puissance_raccordement_soutirage":
        "Puissance de raccordement",
    "compteur.raccordement.branchement.triphase": "Branchement triphasé",
    "compteur.raccordement.puissance_raccordement_soutirage_unite":
        "Unité de la puissance de raccordement",
    "compteur.mesures.pmax_par_phase_kva": "Puissance maximale par phase",
    "optimisation.pas_de_1_kva_c5": "Paliers de puissance de 1 en 1 kVA",
    "comptage.plages_horaires.heures_creuses": "Plages d'heures creuses",
    "comptage.plages_horaires.heures_pointe_fixe": "Plages d'heures de pointe",
    "data.date_from": "Début de la période analysée",
    "data.date_to": "Fin de la période analysée",
    "optimisation.marge_securite_pct": "Marge de sécurité",
    "optimisation.allow_cmdps": "Scénario des dépassements",
    // Revue du 19/10/2026 (N8) : les noms des réglages d'expert.
    // Démo du 20/10 (point 2) : le plancher relève le poste le plus haut
    // (C4, le cas de la page) ; le talon, chaque poste.
    "optimisation.plancher_modulation": "Puissance la plus haute au moins",
    "optimisation.talon_minimum": "Plancher de chaque poste",
    "optimisation.bornes.raccordement_levee":
        "Puissance supérieure au raccordement autorisée",
    "optimisation.mode": "Objectif de l'étude",
    "optimisation.cible": "Puissances à chiffrer",
}

export function libelleChamp(champ: string | null | undefined): string {
    if (!champ) return "Entrée non nommée"
    return LIBELLE_CHAMP[champ] ?? champ
}

/**
 * Libellés des refus servis par le moteur (§9.1). Utilisés seulement quand le
 * moteur n'a pas servi son propre message : sa phrase, quand elle existe, passe
 * TOUJOURS avant celle-ci.
 *
 * Vague wording du 14/10/2026 (Q28, textes E1 à E8 de Mélodie corrigés) : un
 * message par motif ; E1 en un seul message (la date d'ajout du compteur
 * n'est pas servie) ; E4 dit la période qui ne couvre pas tous les postes (C4 ou C2) ;
 * E5 distingue la saisie de la donnée d'Enedis (revue de nuit du 15/10/2026,
 * E7, d'après courtage v183 `validate_fta_segment.py`) : un segment saisi qui
 * contredit la formule (`segment-incoherent-avec-fta`) ou une formule saisie
 * qui contredit le compteur (`validation-fta-incompatible-segment-source`)
 * sont des erreurs de saisie ; une formule d'Enedis qui contredit le segment
 * d'Enedis (`donnee-fta-source-incompatible-segment`) est à compléter ; E9 n'est
 * plus un échec ; « encore » est retiré pour un segment non couvert ; une
 * panne se dit « pour une raison technique », jamais « de notre côté ».
 */
export const LIBELLE_REFUS: Record<string, string> = {
    // E7.
    "compteur-resilie":
        "Ce compteur n'a pas de contrat en cours chez Enedis. L'étude a besoin d'un contrat à comparer.",
    // E2.
    "collecte-interrompue":
        "Les mesures de ce compteur s'arrêtent avant la période demandée : choisissez une période couverte par les mesures.",
    // E1, un seul message.
    "donnee-aucune-disponible":
        "Aucune mesure n'a été reçue pour ce compteur sur la période demandée. Un compteur tout juste ajouté ne reçoit pas ses mesures tout de suite ; sinon, la collecte est peut-être arrêtée.",
    // E4 (I4) : la période ne couvre pas tous les postes d'un C4 ou d'un C2.
    "donnee-classe-hors-fenetre":
        "La période ne couvre pas tous les postes du compteur : choisissez une période qui couvre les deux saisons.",
    "donnee-plage-sans-puissance":
        "Un poste n'a aucune mesure sur la période : aucune puissance n'est fabriquée pour lui. Saisissez la puissance souscrite de ce poste, ou choisissez une autre période, puis relancez.",
    "param-puissance-max-c5-manquante":
        "Enedis n'a transmis aucune puissance maximale atteinte sur la période.",
    // E3.
    "validation-fta-manquante":
        "Formule d'acheminement (FTA) non trouvée chez Enedis. Choisissez-la ci-dessous : elle figure sur le contrat ou une facture du site.",
    // E5, formule saisie qui contredit le compteur.
    "validation-fta-incompatible-segment-source":
        "La formule choisie ne va pas avec le segment de ce compteur : une formule basse tension jusqu'à 36 kVA va avec un C5, au-delà de 36 kVA avec un C4, une formule haute tension avec un C2. Choisissez la formule qui figure sur le contrat ou une facture du site.",
    // E5, formule et segment transmis par Enedis contradictoires.
    "donnee-fta-source-incompatible-segment":
        "La formule et le segment transmis par Enedis pour ce compteur ne vont pas ensemble : une formule basse tension jusqu'à 36 kVA va avec un C5, au-delà de 36 kVA avec un C4, une formule haute tension avec un C2. Saisissez la formule qui figure sur le contrat ou une facture du site.",
    // E5, segment saisi qui contredit la formule (E9 de la revue de nuit :
    // le moteur lève ce code pour tout écart, dans les deux sens).
    "segment-incoherent-avec-fta":
        "Le segment choisi ne va pas avec la formule : une formule basse tension jusqu'à 36 kVA va avec un C5, au-delà de 36 kVA avec un C4, une formule haute tension avec un C2. Corrigez le segment ou la formule.",
    "validation-denivele-ou-puissance":
        "Un C5 se décrit par une puissance, un C4 ou un C2 par une puissance souscrite pour chaque poste. Corrigez les puissances saisies.",
    "validation-cible-manquante":
        "Les puissances à chiffrer ne sont pas saisies.",
    // E8, segment non couvert, sans « encore ».
    "segment-hors-perimetre":
        "L'étude ne couvre pas ce segment de compteur : il n'y a rien à corriger ici.",
    "segment-source-indetermine":
        "Enedis n'a pas déterminé le segment tarifaire de ce compteur.",
    "producteur-pur-hors-perimetre":
        "Ce point n'a que de l'injection : l'étude porte sur le soutirage.",
    // DEV-2311 : compteur qui n'est pas d'électricité, refusé par le moteur
    // (courtage#274) ou dès le lancement par api-metier (#1494).
    "compteur-non-electrique-hors-perimetre":
        "Ce compteur ne mesure pas une consommation d'électricité. L'étude porte sur un compteur d'électricité.",
    error_optimturpe_compteur_non_electrique:
        "Ce compteur ne mesure pas une consommation d'électricité. L'étude porte sur un compteur d'électricité.",
    "param-pointe-mobile-non-couverte":
        "La pointe mobile n'est pas couverte par l'étude : ce compteur n'est pas chiffré en pointe fixe à sa place.",
    "param-puissance-max-hors-catalogue-c5":
        "Aucune puissance souscrite ne couvre la puissance maximale atteinte. Cela relève du raccordement, pas de la souscription.",
    "param-raccordement-unite-inconnue":
        "L'unité de la puissance de raccordement transmise n'est pas reconnue. Unités admises : kVA, kW, VA, W.",
    "param-raccordement-invraisemblable":
        "La puissance de raccordement transmise n'est pas plausible pour ce compteur. Vérifiez la valeur et son unité.",
    "scenario-hors-segment-c4":
        "Un C4 garde au moins une puissance au-dessus de 36 kVA. Les puissances saisies n'en ont aucune.",
    // E6.
    "scenario-denivele-non-croissant":
        "La puissance d'un poste ne peut pas être inférieure à celle du poste précédent.",
    "validation-plancher-hors-segment":
        "La puissance minimale imposée est au-dessus de la puissance de raccordement : aucune combinaison de puissances souscrites ne tient les deux.",
    "validation-fenetre-invalide":
        "La date de début doit précéder la date de fin.",
    "validation-fenetre-trop-longue": "La période demandée est trop longue.",
    // E8, calcul interrompu (W89).
    "analyse-trop-longue":
        "Le calcul a dépassé son délai pour une raison technique, sans lien avec vos données. Relancez l'étude.",
    "analyse-calcul-echoue":
        "Le calcul a échoué pour une raison technique, sans lien avec vos données. Relancez l'étude.",
    "analyse-interrompue-avant-terme":
        "Le calcul s'est interrompu avant son terme pour une raison technique, sans lien avec vos données. Relancez l'étude.",
    "analyse-non-mise-en-file":
        "L'étude n'a pas pu démarrer pour une raison technique. Relancez-la.",
    "analyse-reprise-echouee":
        "La reprise du calcul a échoué pour une raison technique, sans lien avec vos données. Relancez l'étude.",
    "module-v2-desactive":
        "Le calcul de l'étude est momentanément arrêté pour une raison technique. Réessayez plus tard.",
}

/**
 * L'état d'un motif d'échec (Q28 : six états, sans « En attente », qui ne se
 * calcule pas). Seuls quatre états qualifient un échec ; « Prêt » et « Valeur
 * par défaut, à vérifier » qualifient une valeur d'entrée, pas un refus. Un
 * code absent de la table n'a pas d'état : la page ne le devine pas.
 */
export type EtatDEchec =
    | "À compléter"
    | "Erreur de saisie"
    | "Non couvert"
    | "Erreur technique"

export const ETAT_DU_MOTIF_D_ECHEC: Record<string, EtatDEchec> = {
    "donnee-aucune-disponible": "À compléter",
    "collecte-interrompue": "À compléter",
    "donnee-classe-hors-fenetre": "À compléter",
    "donnee-plage-sans-puissance": "À compléter",
    "validation-fta-manquante": "À compléter",
    "donnee-fta-source-incompatible-segment": "À compléter",
    "param-plages-heures-creuses-manquantes": "À compléter",
    "segment-incoherent-avec-fta": "Erreur de saisie",
    "validation-fta-incompatible-segment-source": "Erreur de saisie",
    "scenario-denivele-non-croissant": "Erreur de saisie",
    "scenario-hors-segment-c4": "Erreur de saisie",
    "validation-denivele-ou-puissance": "Erreur de saisie",
    "validation-cible-manquante": "Erreur de saisie",
    "validation-plancher-hors-segment": "Erreur de saisie",
    "validation-fenetre-invalide": "Erreur de saisie",
    "validation-fenetre-trop-longue": "Erreur de saisie",
    "compteur-resilie": "Non couvert",
    "segment-hors-perimetre": "Non couvert",
    "producteur-pur-hors-perimetre": "Non couvert",
    "compteur-non-electrique-hors-perimetre": "Non couvert",
    error_optimturpe_compteur_non_electrique: "Non couvert",
    "param-pointe-mobile-non-couverte": "Non couvert",
    "param-puissance-max-hors-catalogue-c5": "Non couvert",
    "analyse-trop-longue": "Erreur technique",
    "analyse-calcul-echoue": "Erreur technique",
    "analyse-interrompue-avant-terme": "Erreur technique",
    "analyse-non-mise-en-file": "Erreur technique",
    "analyse-reprise-echouee": "Erreur technique",
    "module-v2-desactive": "Erreur technique",
    "optimturpe-v2-execution-fermee": "Erreur technique",
}

/**
 * Rapport ou courbe demandés sur une étude qui n'a pas de résultat (DEV-2311,
 * refus 409 du moteur, courtage#274). Utilisés seulement quand la façade ne
 * sert pas sa description.
 */
export const LIBELLE_ETUDE_SANS_DOCUMENT: Record<string, string> = {
    "optimturpe-rapport-etude-en-cours":
        "Le calcul de cette étude n'est pas encore terminé. Le rapport sera disponible dès la fin du calcul.",
    "optimturpe-rapport-etude-sans-resultat":
        "Cette étude s'est terminée sur un refus : elle n'a pas de résultat chiffré, donc pas de rapport. La cause du refus est indiquée sur l'étude.",
    "optimturpe-serie-etude-en-cours":
        "Le calcul de cette étude n'est pas encore terminé. La courbe sera disponible dès la fin du calcul.",
    "optimturpe-serie-etude-sans-resultat":
        "Cette étude s'est terminée sur un refus : elle n'a pas de puissances recommandées sur lesquelles lire la courbe. La cause du refus est indiquée sur l'étude.",
}

/**
 * Le levier que le client peut actionner après un refus (§9.1, bloquant B9).
 * `aucun` est une valeur pleine : elle dit qu'il n'y a rien à corriger ici, ce
 * qui n'est pas la même chose que de ne pas savoir.
 */
export const LIBELLE_LEVIER: Record<string, string> = {
    fta: "Corrigez la formule d'acheminement (FTA)",
    dénivelé: "Corrigez les puissances souscrites par poste",
    denivele: "Corrigez les puissances souscrites par poste",
    fenêtre: "Changez la période analysée",
    fenetre: "Changez la période analysée",
    "heures creuses": "Corrigez les plages d'heures creuses",
    raccordement: "Corrigez la puissance de raccordement",
    branchement: "Corrigez le type de branchement",
    "régime de pointe": "Corrigez le choix pointe fixe ou pointe mobile",
    "regime de pointe": "Corrigez le choix pointe fixe ou pointe mobile",
    scénario: "Corrigez les puissances saisies",
    scenario: "Corrigez les puissances saisies",
    aucun: "Cette cause ne se corrige pas depuis cette page.",
}

// =============================================================================
// Le dictionnaire des libellés client de la page (DEV-2218, revue des études
// v148 du 28/09/2026). Un seul endroit pour les noms de scénarios, de postes, de
// statistiques et d'options : un sigle interne n'y apparaît qu'entre
// parenthèses, derrière son nom en toutes lettres, et une seule fois par carte.
// =============================================================================

/** Les deux scénarios de l'arbitrage, nommés par ce que le client choisit. */
export const LIBELLE_SCENARIO_ARBITRAGE = {
    sans: "Couvrir les dépassements",
    avec: "Assumer les dépassements",
    paire: "Couvrir ou assumer les dépassements",
} as const

/** Le coût estimé des dépassements de puissance souscrite (la CMDPS), avec son sigle. */
export const LIBELLE_DEPASSEMENTS_ESTIMES_SIGLE =
    "Dépassements de puissance estimés (CMDPS)"

/**
 * La part de l'économie liée à la formule et aux puissances souscrites, hors
 * dépassement (lexique client v0.1 du 05/10/2026 : ni « ferme » ni
 * « démontrable » devant le client).
 */
/**
 * La ligne de total de « D'où vient ce montant » (revue du support du 08/10,
 * A3) : « total de la part » supposait une autre partie quelque part. La ligne
 * suivante dit les dépassements « hors de ce total ».
 */
export const LIBELLE_TOTAL_HORS_DEPASSEMENTS = "Total hors dépassements"

/** Chaque poste du TURPE en toutes lettres, son sigle entre parenthèses. */
export const LIBELLE_POSTE_TURPE: Record<string, string> = {
    // Vague wording du 14/10/2026 (partie 4, W41) : le nom compris d'abord,
    // le sigle ensuite.
    turpeCSF: "Part puissance (CSF)",
    turpeCSV: "Part énergie (CSV)",
    turpeCER: "Composante d'énergie réactive (CER)",
    turpeCG: "Composante de gestion (CG)",
    turpeCC: "Composante de comptage (CC)",
    turpeCMDPS: LIBELLE_DEPASSEMENTS_ESTIMES_SIGLE,
    cta: "Contribution tarifaire d'acheminement (CTA)",
}

/** Les options tarifaires servies sous une valeur d'énumération. */
export const LIBELLE_OPTION_TARIFAIRE: Record<string, string> = {
    HS_PTE_Fixe: "pointe fixe",
    HS_PTE_Mobile: "pointe mobile",
}

/** Le nom client d'une option tarifaire ; une valeur inconnue reste telle quelle. */
export function libelleOptionTarifaire(valeur: unknown): unknown {
    return typeof valeur === "string"
        ? (LIBELLE_OPTION_TARIFAIRE[valeur] ?? valeur)
        : valeur
}

/** « 95 % du temps sous 40 kVA » : un centile dit par ce qu'il mesure. */
export function libelleTempsSous(pourcent: number, niveau: string): string {
    return `${pourcent}\u00a0% du temps sous ${niveau}`
}

/**
 * La part du dépassement tombée de décembre à février (`MOIS_HIVER` du moteur,
 * décision de Mathieu du 28/09 : inchangé). Elle ne se dit pas « d'hiver » à
 * côté de mois de saison haute (novembre à mars).
 */
export const LIBELLE_PART_DECEMBRE_FEVRIER =
    "part de décembre à février du dépassement"

/**
 * Une durée en heures, jamais arrondie à « 0 h » quand elle n'est pas nulle
 * (DEV-2214 : 0,17 h mesurée s'imprimait « 0 h »).
 */
export function libelleDureeHeures(heures: unknown): string | null {
    if (typeof heures !== "number" || !Number.isFinite(heures)) return null
    if (heures > 0 && heures < 1) return "moins d'une heure"
    const texte = formateurFr({
        maximumFractionDigits: 1,
    }).format(heures)
    return `${texte}\u00a0h`
}

/**
 * Libellé d'un compteur au sélecteur de la page : « C4 — 300 022 … » quand le
 * segment est connu ; sinon « Compteur 300 022 … », jamais un tiret à la place
 * du segment (finitions 2 du 07/10/2026 : « - — 171 858 … » se lisait comme
 * une valeur manquante sans dire laquelle).
 */
export function libelleOptionCompteur(
    segment: string | null | undefined,
    numero: string
): string {
    const seg = segment?.trim() ?? ""
    if (seg !== "" && seg !== "-" && seg !== "—") return `${seg} — ${numero}`
    return numero.trim() !== "" ? `Compteur ${numero}` : "Compteur"
}
